import { storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { constructQueryParamName, isOriginInUse } from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { getFacets } from '../../util/api';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { searchParamsPicker } from './SearchPage.shared';
import { isFilterActive } from './FilterBar/filterBarHelpers';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 24;

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const FETCH_FACETS_SUCCESS = 'app/SearchPage/FETCH_FACETS_SUCCESS';
export const FETCH_EXCEPT_FIELD_PRESENCE_SUCCESS =
  'app/SearchPage/FETCH_EXCEPT_FIELD_PRESENCE_SUCCESS';

// Option lists kept for the searches seen this visit (the oldest go first)
const EXCEPT_FIELD_PRESENCE_LIMIT = 40;

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  // The latest search asked for: an older answer arriving after it is dropped.
  searchRequest: 0,
  // v1 facets: { total, scanned, exact, presence, categoryCounts } or null
  facets: null,
  // A filtered field's own option list, worked out against the OTHER filters
  // (exceptFieldQuery's key → the field's values present)
  exceptFieldPresence: {},
};

const resultIds = data => data.data.map(l => l.id);

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchRequest: payload.request,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return action.meta?.request !== state.searchRequest
        ? state
        : {
            ...state,
            currentPageResultIds: resultIds(payload.data),
            pagination: payload.data.meta,
            searchInProgress: false,
          };
    case SEARCH_LISTINGS_ERROR:
      if (action.meta?.request !== state.searchRequest) return state;
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };

    case FETCH_FACETS_SUCCESS:
      return { ...state, facets: payload };
    case FETCH_EXCEPT_FIELD_PRESENCE_SUCCESS: {
      const kept = Object.entries(state.exceptFieldPresence)
        .filter(([key]) => key !== payload.key)
        .slice(-(EXCEPT_FIELD_PRESENCE_LIMIT - 1));
      return {
        ...state,
        exceptFieldPresence: { ...Object.fromEntries(kept), [payload.key]: payload.values },
      };
    }

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = (searchParams, request) => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams, request },
});

export const searchListingsSuccess = (response, request) => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
  meta: { request },
});

export const searchListingsError = (e, request) => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
  meta: { request },
});

let searchRequests = 0;
export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  searchRequests += 1;
  const request = searchRequests;
  dispatch(searchListingsRequest(searchParams, request));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  // showSoldOut is a UI-only URL param that controls whether sold-out listings are
  // included in results. It is extracted here so it never reaches the Sharetribe API
  // as an unknown param (same pattern as sort, price, dates).
  // Note: parse() in urlHelpers converts the string 'true' to boolean true, so both
  // forms are checked.
  //
  // When showSoldOut is active we query pub_soldOut=true (publicData flag) rather
  // than a stock-level filter. This is the only way to return ONLY sold-out listings —
  // Sharetribe's stock API has no maxStock param. The soldOut flag is kept in sync
  // everywhere stock changes via soldOutSync.js.
  const stockFilters = (datesMaybe, showSoldOut) => {
    const hasDatesFilterInUse = Object.keys(datesMaybe).length > 0;
    if (hasDatesFilterInUse) {
      // When a dates filter is active, showSoldOut is intentionally ignored.
      // Sold-out listings have no stock/availability and would not match date-range
      // queries anyway, so the toggle is a no-op in this context.
      return { minStock: 1, stockMode: 'match-undefined' };
    }
    if (showSoldOut === true || showSoldOut === 'true') {
      // pub_soldOut=true returns only listings with soldOut: true in publicData.
      // No minStock/stockMode needed — this is a publicData filter, not a stock filter.
      return { pub_soldOut: true };
    }
    return { minStock: 1, stockMode: 'match-undefined' };
  };

  const { perPage, price, dates, sort, showSoldOut, ...rest } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const stockMaybe = stockFilters(datesMaybe, showSoldOut);
  const sortMaybe = sort === config.search.sortConfig.relevanceKey ? {} : { sort };

  const params = {
    ...rest,
    ...priceMaybe,
    ...datesMaybe,
    ...stockMaybe,
    ...sortMaybe,
    ...searchValidListingTypes(config.listing.listingTypes),
    perPage,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(searchListingsSuccess(response, request));
      return response;
    })
    .catch(e => {
      dispatch(searchListingsError(storableError(e), request));
      throw e;
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

// /api/facets scans up to 5 upstream pages, so a slow earlier response can
// land AFTER a newer one during rapid filter changes; only the latest request
// is allowed to write state (mirrors searchParamsAreInSync for listings).
let latestFacetsRequest = 0;
export const fetchFacets = rawParams => dispatch => {
  const requestId = ++latestFacetsRequest;
  const cleaned = Object.keys(rawParams || {}).reduce((acc, key) => {
    const value = rawParams[key];
    if (value !== undefined && value !== null && typeof value !== 'object') acc[key] = value;
    return acc;
  }, {});
  return Promise.resolve()
    .then(() => getFacets(cleaned))
    .then(response => {
      if (requestId !== latestFacetsRequest) return;
      dispatch({ type: FETCH_FACETS_SUCCESS, payload: response || null });
    })
    .catch(() => {
      if (requestId !== latestFacetsRequest) return;
      dispatch({ type: FETCH_FACETS_SUCCESS, payload: null });
    });
};

/**
 * The search a filtered field's own option list comes from: every filter but its own
 * (the active-field exception of faceted search: one pick neither collapses the list
 * to itself nor shows every option ever). key names the list in the store.
 */
export const exceptFieldQuery = (urlQueryParams, filterConfig) => {
  const paramName = constructQueryParamName(filterConfig.key, filterConfig.scope || 'public');
  const params = {};
  Object.keys(urlQueryParams || {}).forEach(name => {
    const value = urlQueryParams[name];
    if (name !== paramName && value !== undefined && value !== null && typeof value !== 'object') {
      params[name] = value;
    }
  });
  return { params, key: `${paramName}|${JSON.stringify(params)}` };
};

/** A filtered field's own option list (exceptFieldQuery), once per search; a failure leaves none. */
export const fetchExceptFieldPresence = (urlQueryParams, filterConfig) => (dispatch, getState) => {
  const { params, key } = exceptFieldQuery(urlQueryParams, filterConfig);
  if (getState().SearchPage.exceptFieldPresence[key]) return Promise.resolve();
  return getFacets(params)
    .then(response => {
      const values = response?.presence?.[filterConfig.key] || [];
      dispatch({ type: FETCH_EXCEPT_FIELD_PRESENCE_SUCCESS, payload: { key, values } });
    })
    .catch(() => {
      // The field shows its usual list
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const { page = 1, address, origin, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const searchParams = {
    ...rest,
    ...originMaybe,
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.listing': [
      'title',
      'geolocation',
      'price',
      'publicData',
      'publicData.listingType',
      'publicData.transactionProcessAlias',
      'publicData.unitType',
      'publicData.pickupEnabled',
      'publicData.shippingEnabled',
    ],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'profile.publicData'],
    ...createImageVariantConfig('listing-card', 400, 1),
    ...createImageVariantConfig('listing-card-2x', 800, 1),
    'limit.images': 5,
  };

  // v1 facets (presence) for the filter panel + category facet sidebar —
  // raw UI params; the server does price/soldOut/stock conversions.
  dispatch(fetchFacets(rest));

  // Each filtered field's own option list, ready for when its pill or panel opens
  const { listingFields: listingFieldsConfig = [] } = config?.listing || {};
  const { defaultFilters: defaultFiltersConfig = [], sortConfig } = config?.search || {};
  const { urlQueryParams } = searchParamsPicker(
    search,
    {},
    listingFieldsConfig,
    defaultFiltersConfig,
    sortConfig,
    isOriginInUse(config)
  );
  listingFieldsConfig
    .filter(
      field =>
        field.filterConfig?.indexForSearch &&
        field.enumOptions?.length > 0 &&
        isFilterActive(field, urlQueryParams)
    )
    .forEach(field => dispatch(fetchExceptFieldPresence(urlQueryParams, field)));

  return dispatch(searchListings(searchParams, config));
};
